/*
  EasyUSA "It's easy." buying walkthrough
  (sections/easyusa-buying-explainer.liquid).

  One shape, never cut, walks the real buying flow in five chapters while a
  cursor drives each change:
    1 Easy to get in   email → one-time code → pricing unlocked
    2 Easy to find     search → results → colour filter → open the match
    3 Easy to order    product card (+ cases) → add to cart → Quick Order
    4 Easy to receive  shipping options → free local delivery → tracking
    5 Easy to reorder  last order → Buy again → back to the email field

  Every style is computed from the loop time inside seek(t): no CSS
  transitions, no state carried between frames. Springs are closed-form step
  responses, and a value that changes target several times is the sum of one
  spring per change, so any frame can be drawn in isolation. That lets the
  chapter bar jump anywhere, and makes the last frame match the first so the
  loop never stutters.

  The timeline is authored in LOOP seconds and played slower (RATE), with a
  short hold on each resolved state so people can read it. Plays only while
  on screen. Reduced motion: no autoplay; the scene holds on a chapter and
  the chapter bar moves between them.
*/
(function () {
  'use strict';

  // ── Math ───────────────────────────────────────────────────────
  function stepResponse(t, omega, zeta) {
    if (t <= 0) return 0;
    if (zeta >= 1) return 1 - Math.exp(-omega * t) * (1 + omega * t);
    var wd = omega * Math.sqrt(1 - zeta * zeta);
    return 1 - Math.exp(-zeta * omega * t) *
      (Math.cos(wd * t) + (zeta * omega / wd) * Math.sin(wd * t));
  }
  function smooth(a, b, t) {
    if (t <= a) return 0;
    if (t >= b) return 1;
    var x = (t - a) / (b - a);
    return x * x * x * (x * (x * 6 - 15) + 10);
  }
  // Up over [a, a+rise], back down over [b-fall, b].
  function pulse(a, b, t, rise, fall) {
    return smooth(a, a + (rise || 0.06), t) * (1 - smooth(b - (fall || 0.12), b, t));
  }
  function lerp(a, b, k) { return a + (b - a) * k; }
  function clamp01(v) { return v < 0 ? 0 : v > 1 ? 1 : v; }

  // A channel is a start value plus a list of [time, target, omega?, zeta?].
  function channel(v0, list, omega, zeta) {
    var prev = v0;
    var changes = [];
    list.forEach(function (c) {
      if (c[1] === prev) return;
      changes.push({ t: c[0], d: c[1] - prev, w: c[2] || omega, z: c[3] || zeta });
      prev = c[1];
    });
    return { v0: v0, changes: changes };
  }
  function valueAt(ch, t) {
    var v = ch.v0;
    for (var i = 0; i < ch.changes.length; i++) {
      var c = ch.changes[i];
      v += c.d * stepResponse(t - c.t, c.w, c.z);
    }
    return v;
  }
  function hexRgb(hex) {
    var n = parseInt(hex.slice(1), 16);
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
  }
  function px(el, prop) { return el ? parseFloat(el.style[prop]) || 0 : 0; }

  // ── Timeline (authored seconds) ────────────────────────────────
  var LOOP = 22.15;
  var CHAPTERS = [0, 4.95, 8.5, 15.5, 19.45, LOOP];
  // A calm moment inside each chapter for reduced-motion still frames.
  var HOLD = [4.7, 7.6, 12.2, 17.9, 20.9];

  // Playback: slower than authored, holding on each resolved state.
  // [authored time, hold seconds]
  var RATE = 0.8;
  var PAUSES = [
    [1.45, 0.5], [3.25, 0.4], [4.6, 0.8],     // email typed, code ok, unlocked
    [5.88, 0.4], [7.45, 0.7],                  // "canvas" typed, filtered
    [9.8, 0.5], [10.4, 0.4], [12.1, 0.6],      // 3 cases, Add to Cart, Added
    [15.0, 0.8],                               // Quick Order added
    [17.8, 0.9], [19.2, 0.6],                  // free local delivery, delivered
    [20.9, 0.9]                                // bought again
  ];
  var REAL_LOOP = LOOP / RATE + PAUSES.reduce(function (sum, p) { return sum + p[1]; }, 0);

  function authoredAt(clock) {
    var a = 0;
    var r = 0;
    for (var i = 0; i < PAUSES.length; i++) {
      var run = (PAUSES[i][0] - a) / RATE;
      if (clock < r + run) return a + (clock - r) * RATE;
      r += run;
      if (clock < r + PAUSES[i][1]) return PAUSES[i][0];
      r += PAUSES[i][1];
      a = PAUSES[i][0];
    }
    return Math.min(LOOP, a + (clock - r) * RATE);
  }
  function clockAt(t) {
    var a = 0;
    var r = 0;
    for (var i = 0; i < PAUSES.length; i++) {
      if (t <= PAUSES[i][0]) return r + (t - a) / RATE;
      r += (PAUSES[i][0] - a) / RATE + PAUSES[i][1];
      a = PAUSES[i][0];
    }
    return r + (t - a) / RATE;
  }

  var NAVY = '#0d1e30';
  var BLUE = '#2e7ec4';
  var GREEN = '#4a7225';
  var WHITE = '#ffffff';

  // The shape's targets: [time, width, height, radius, colour].
  var SHAPE = [
    [0, 340, 58, 29, WHITE],       // 1 email field
    [2.0, 300, 68, 16, WHITE],     //   code slots
    [3.45, 300, 60, 30, NAVY],     //   pricing unlocked
    [4.95, 360, 56, 28, WHITE],    // 2 search field
    [6.05, 360, 272, 20, WHITE],   //   results
    [8.5, 340, 224, 20, WHITE],    // 3 product card
    [9.85, 220, 52, 12, BLUE],     //   add to cart
    [10.6, 52, 52, 26, BLUE],      //   loading
    [11.2, 52, 52, 26, GREEN],     //   confirmed
    [11.65, 220, 52, 12, GREEN],   //   "Added · 3"
    [12.6, 420, 292, 20, WHITE],   //   Quick Order (colour × size matrix)
    [15.5, 330, 240, 20, WHITE],   // 4 shipping
    [18.0, 380, 96, 22, WHITE],    //   tracking
    [19.45, 340, 164, 20, WHITE],  // 5 last order
    [21.4, 340, 58, 29, WHITE]     //   back to the email field
  ];

  function shapeChannels() {
    var first = SHAPE[0];
    var rgb0 = hexRgb(first[4]);
    var lists = { w: [], h: [], r: [], c0: [], c1: [], c2: [] };
    SHAPE.slice(1).forEach(function (s) {
      var rgb = hexRgb(s[4]);
      lists.w.push([s[0], s[1]]);
      lists.h.push([s[0], s[2]]);
      lists.r.push([s[0], s[3]]);
      lists.c0.push([s[0], rgb[0]]);
      lists.c1.push([s[0], rgb[1]]);
      lists.c2.push([s[0], rgb[2]]);
    });
    return {
      w: channel(first[1], lists.w, 17, 0.84),
      h: channel(first[2], lists.h, 17, 0.84),
      r: channel(first[3], lists.r, 17, 0.9),
      c0: channel(rgb0[0], lists.c0, 16, 1),
      c1: channel(rgb0[1], lists.c1, 16, 1),
      c2: channel(rgb0[2], lists.c2, 16, 1)
    };
  }
  var SHAPE_CH = shapeChannels();

  // Content layers: visible windows [in, out]. Content enters a beat after
  // the shape starts moving and exits before it does, so swaps never overlap.
  var LAYERS = {
    email: [[-1, 1.95], [21.5, LOOP + 1]],
    code: [[2.05, 3.42]],
    unlock: [[3.5, 4.92]],
    search: [[5.0, 6.02]],
    results: [[6.1, 8.47]],
    tile: [[8.55, 9.83]],
    atc: [[9.9, 10.58]],
    spinner: [[10.65, 11.27]],
    check: [[11.2, 11.67]],
    added: [[11.7, 12.58]],
    qo: [[12.65, 15.47]],
    ship: [[15.68, 17.97]],
    track: [[18.05, 19.42]],
    reorder: [[19.5, 21.37]]
  };
  // Scene captions under the stage: [caption number, windows]. The first
  // also covers the return to the email field so the loop stays seamless.
  var CAPTIONS = {
    1: [[-1, 3.45], [21.45, LOOP + 1]],
    2: [[3.45, 4.95]],
    3: [[4.95, 8.5]],
    4: [[8.5, 12.6]],
    5: [[12.6, 15.5]],
    6: [[15.5, 18.0]],
    7: [[18.0, 19.45]],
    8: [[19.45, 21.45]]
  };
  function captionAlpha(windows, t) {
    for (var i = 0; i < windows.length; i++) {
      var w = windows[i];
      if (t >= w[0] && t <= w[1]) {
        return smooth(w[0] + 0.1, w[0] + 0.35, t) * (1 - smooth(w[1] - 0.2, w[1], t));
      }
    }
    return 0;
  }

  function layerAlpha(windows, t) {
    for (var i = 0; i < windows.length; i++) {
      var w = windows[i];
      if (t >= w[0] && t <= w[1]) {
        return smooth(w[0] + 0.04, w[0] + 0.24, t) * (1 - smooth(w[1] - 0.13, w[1], t));
      }
    }
    return 0;
  }

  // Cursor: [time, x, y] from the stage centre. Named targets are filled in
  // per instance from the rendered markup (real products change layouts).
  var CURSOR = [
    [0, 150, 110], [0.25, 150, 110],
    [0.5, -70, 6], [1.3, -70, 6],            // click into the email field
    [1.58, 116, 4], [2.0, 116, 4],           // "Send code"
    [2.5, 170, 92], [5.05, 170, 92],
    [5.28, -120, 6], [5.9, -120, 6],         // click the search field, type
    [6.75, 'chip'], [7.0, 'chip'],           // tap the colour filter
    [7.75, 'match'], [8.1, 'match'],         // open the match
    [8.95, 'plus'], [9.75, 'plus'],          // + case, + case
    [10.15, 20, 8], [10.6, 20, 8],           // Add to Cart
    [10.9, 110, 72], [12.85, 180, 110],
    [13.05, 'qty0'], [13.2, 'qty0'],         // Quick Order: type case counts
    [13.45, 'qty1'], [13.6, 'qty1'],
    [13.85, 'qty2'], [14.0, 'qty2'],
    [14.25, 'qty3'], [14.4, 'qty3'],
    [14.7, 'qoBtn'], [15.2, 'qoBtn'],        // Add selected
    [15.8, 104, -49], [16.2, 104, -3],       // glide down the shipping options
    [16.6, 104, 43], [17.0, 104, 89],
    [17.4, 104, 43], [17.75, 104, 43],       // back up, pick free local delivery
    [18.25, 210, 112], [19.5, 210, 112],
    [19.95, 'buyAgain'], [20.3, 'buyAgain'], // Buy again
    [20.7, 200, 110], [21.5, 200, 110],
    [21.95, 150, 110], [LOOP, 150, 110]
  ];
  var CLICKS = [0.52, 1.64, 5.3, 6.85, 7.9, 9.1, 9.4, 10.5,
    13.1, 13.5, 13.9, 14.3, 14.8, 17.55, 20.05];

  // Quick Order case counts typed into each row, and when.
  var QO_TYPE = [13.12, 13.52, 13.92, 14.32];
  var QO_QTY = [3, 4, 2, 3];

  // Shipping highlight: each edge rides its own spring, the leading edge
  // faster, so it stretches toward the next row. Picks free local delivery.
  var ROW_Y = [-49, -3, 43, 89];
  var ROW_H = 42;
  var SHIP_PICK = 2;
  var SHIP_VISITS = [[15.8, 0], [16.2, 1], [16.6, 2], [17.0, 3], [17.4, SHIP_PICK]];
  function edgeChannels() {
    var fast = [26, 0.9];
    var slow = [13, 0.95];
    var top = [];
    var bottom = [];
    var prev = 0;
    SHIP_VISITS.slice(1).forEach(function (v) {
      var down = v[1] > prev;
      var topSpring = down ? slow : fast;
      var bottomSpring = down ? fast : slow;
      top.push([v[0], ROW_Y[v[1]] - ROW_H / 2, topSpring[0], topSpring[1]]);
      bottom.push([v[0], ROW_Y[v[1]] + ROW_H / 2, bottomSpring[0], bottomSpring[1]]);
      prev = v[1];
    });
    return {
      top: channel(ROW_Y[0] - ROW_H / 2, top, 13, 0.95),
      bottom: channel(ROW_Y[0] + ROW_H / 2, bottom, 13, 0.95)
    };
  }
  var EDGES = edgeChannels();

  function chapterIndex(t) {
    for (var i = 0; i < CHAPTERS.length - 2; i++) if (t < CHAPTERS[i + 1]) return i;
    return CHAPTERS.length - 2;
  }

  // ── Instance ───────────────────────────────────────────────────
  function Explainer(root) {
    this.root = root;
    var q = function (sel) { return root.querySelector(sel); };
    var qa = function (sel) { return root.querySelectorAll(sel); };
    this.stage = q('[data-bx-stage]');
    this.scaler = q('[data-bx-scale]');
    this.shape = q('[data-bx-shape]');
    this.cursor = q('[data-bx-cursor]');
    this.layers = {};
    var self = this;
    qa('[data-bx-layer]').forEach(function (el) {
      self.layers[el.getAttribute('data-bx-layer')] = el;
    });

    // 1 Get in
    this.email = root.getAttribute('data-bx-email') || 'buyer@yourstore.com';
    this.typed = q('[data-bx-typed]');
    this.placeholder = q('[data-bx-placeholder]');
    this.caret = q('[data-bx-caret]');
    this.sendBtn = q('[data-bx-send]');
    this.slots = qa('[data-bx-slot]');
    this.shackle = q('[data-bx-shackle]');
    this.unlockText = q('[data-bx-unlock-text]');

    // 2 Find
    this.term = root.getAttribute('data-bx-term') || '';
    this.searchTyped = q('[data-bx-search-typed]');
    this.searchCaret = q('[data-bx-search-caret]');
    this.chips = qa('[data-bx-chip]');
    this.chipPick = Math.max(0, Array.prototype.findIndex.call(this.chips, function (c) {
      return c.hasAttribute('data-bx-chip-pick');
    }));
    this.resultRows = qa('[data-bx-result]');
    // "1 of 3 styles in Pink" — counted from the rendered matches.
    this.resultCount = q('[data-bx-result-count]');
    if (this.resultCount) {
      var matchCount = qa('[data-bx-result][data-bx-match]').length;
      this.resultCount.textContent = matchCount + ' of ' + this.resultRows.length +
        (this.resultRows.length === 1 ? ' style' : ' styles') + ' in ' + this.resultCount.getAttribute('data-filter');
    }

    // 3 Order
    this.ring = q('[data-bx-ring]');
    this.photos = qa('[data-bx-photo]');
    var dots = qa('[data-bx-dot]');
    this.pick = Math.min(parseInt(root.getAttribute('data-bx-pick'), 10) || 0, Math.max(0, dots.length - 1));
    if (dots[this.pick]) {
      this.ring.style.left = px(dots[this.pick], 'left') + 'px';
      this.ring.style.top = px(dots[this.pick], 'top') + 'px';
    }
    this.qty = q('[data-bx-qty]');
    this.plus = q('[data-bx-plus]');
    this.spin = q('[data-bx-spin]');
    this.checkPath = q('[data-bx-check]');
    // Quick Order matrix: type into one cell per colour row, stepping across
    // the sizes that colour comes in so the entries land in different
    // columns; then top up from the remaining cells if there are few rows.
    this.qoRows = qa('[data-bx-qo-row]');
    this.qoCells = qa('[data-bx-qo-cell]');
    var chosen = [];
    var byRow = {};
    Array.prototype.forEach.call(this.qoCells, function (cell) {
      (byRow[cell.dataset.row] = byRow[cell.dataset.row] || []).push(cell);
    });
    Object.keys(byRow).sort(function (x, y) { return x - y; }).forEach(function (row, r) {
      var cells = byRow[row];
      if (chosen.length < QO_TYPE.length) chosen.push(cells[(r + 2) % cells.length]);
    });
    Array.prototype.forEach.call(this.qoCells, function (cell) {
      if (chosen.length < QO_TYPE.length && chosen.indexOf(cell) < 0) chosen.push(cell);
    });
    this.qoInputs = chosen;
    this.qoBadge = q('[data-bx-qo-badge]');
    this.qoStatus = q('[data-bx-qo-status]');
    this.qoBtn = q('[data-bx-qo-btn]');
    this.qoBtnText = q('[data-bx-qo-btn-text]');

    // 4 Receive
    this.indicator = q('[data-bx-indicator]');
    this.rows = qa('[data-bx-row]');
    this.trackFill = q('[data-bx-track-fill]');
    this.truck = q('[data-bx-truck]');
    this.nodes = qa('[data-bx-node]');

    // 5 Reorder
    this.reorderThumbs = qa('[data-bx-reorder-thumb]');
    this.buyBtn = q('[data-bx-buy]');
    this.buyLabel = q('[data-bx-buy-label]');
    this.buyDone = q('[data-bx-buy-done]');
    this.buySpin = q('[data-bx-buy-spin]');

    this.captionEls = qa('[data-bx-cap]');
    this.segments = qa('[data-bx-segment]');
    this.fills = qa('[data-bx-fill]');
    this.captions = qa('[data-bx-caption]');

    this.path = this.buildPath();
    this.reduce = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    this.time = this.reduce && this.reduce.matches ? HOLD[0] : CHAPTERS[0];
    this.clock = clockAt(this.time);
    this.playing = false;
    this.visible = false;
    this.last = 0;
    this.frame = 0;
    this.lastQty = '';

    this.fit();
    this.bind();
    this.seek(this.time);
  }

  Explainer.prototype.buildPath = function () {
    var root = this.root;
    function at(sel) {
      var el = root.querySelector(sel);
      return el ? [px(el, 'left'), px(el, 'top')] : [0, 0];
    }
    var chip = this.chips[this.chipPick];
    var targets = {
      chip: chip ? [px(chip, 'left'), px(chip, 'top')] : [0, -72],
      match: [0, -22],
      plus: at('[data-bx-plus]'),
      qoBtn: at('[data-bx-qo-btn]'),
      buyAgain: at('[data-bx-buy]')
    };
    Array.prototype.forEach.call(this.qoInputs, function (input, i) {
      targets['qty' + i] = [px(input, 'left'), px(input, 'top')];
    });
    return CURSOR.map(function (w) {
      if (typeof w[1] !== 'string') return w;
      var p = targets[w[1]] || [0, 0];
      return [w[0], p[0], p[1]];
    });
  };

  Explainer.prototype.fit = function () {
    var width = this.stage.clientWidth;
    var scale = Math.min(1.25, width / 520);
    this.scaler.style.transform = 'scale(' + scale + ')';
    this.stage.style.height = Math.round(360 * scale) + 'px';
  };

  Explainer.prototype.bind = function () {
    var self = this;

    if (window.ResizeObserver) {
      new ResizeObserver(function () { self.fit(); }).observe(this.stage);
    } else {
      window.addEventListener('resize', function () { self.fit(); });
    }
    if (window.IntersectionObserver) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        self.update();
      }, { threshold: 0.35 }).observe(this.stage);
    } else {
      this.visible = true;
    }
    this.segments.forEach(function (seg, i) {
      seg.addEventListener('click', function () {
        self.time = self.reduce && self.reduce.matches ? HOLD[i] : CHAPTERS[i];
        self.clock = clockAt(self.time);
        self.last = performance.now();
        self.seek(self.time);
      });
    });
    if (this.reduce && this.reduce.addEventListener) {
      this.reduce.addEventListener('change', function () { self.update(); });
    }
    this.update();
  };

  Explainer.prototype.update = function () {
    var shouldPlay = this.visible && !(this.reduce && this.reduce.matches);
    if (shouldPlay && !this.playing) {
      this.playing = true;
      this.last = performance.now();
      var self = this;
      var tick = function (now) {
        if (!self.playing) return;
        // Clamp so a background tab doesn't jump the loop forward.
        var dt = Math.min(0.05, (now - self.last) / 1000);
        self.last = now;
        self.clock = (self.clock + dt) % REAL_LOOP;
        self.time = authoredAt(self.clock);
        self.seek(self.time);
        self.frame = requestAnimationFrame(tick);
      };
      this.frame = requestAnimationFrame(tick);
    } else if (!shouldPlay && this.playing) {
      this.playing = false;
      cancelAnimationFrame(this.frame);
      if (this.reduce && this.reduce.matches) {
        this.time = HOLD[chapterIndex(this.time)];
        this.clock = clockAt(this.time);
        this.seek(this.time);
      }
    }
  };

  function show(el, a) {
    el.style.opacity = a.toFixed(3);
    el.style.filter = a < 0.999 ? 'blur(' + ((1 - a) * 6).toFixed(2) + 'px)' : '';
    el.style.transform = 'translate(-50%, -50%) scale(' + (0.96 + 0.04 * a).toFixed(4) + ')';
    el.style.visibility = a > 0.001 ? 'visible' : 'hidden';
  }
  function scaleCentered(el, k) {
    el.style.transform = 'translate(-50%, -50%) scale(' + k.toFixed(3) + ')';
  }
  function setText(el, text) {
    if (el.textContent !== text) el.textContent = text;
  }

  // Draw frame t. Pure function of t.
  Explainer.prototype.seek = function (t) {
    var i;

    // ── Shape + layers ──
    var w = valueAt(SHAPE_CH.w, t);
    var h = valueAt(SHAPE_CH.h, t);
    var r = valueAt(SHAPE_CH.r, t);
    var c = [valueAt(SHAPE_CH.c0, t), valueAt(SHAPE_CH.c1, t), valueAt(SHAPE_CH.c2, t)]
      .map(function (v) { return Math.round(Math.max(0, Math.min(255, v))); });
    this.shape.style.width = w.toFixed(2) + 'px';
    this.shape.style.height = h.toFixed(2) + 'px';
    this.shape.style.borderRadius = Math.min(r, h / 2, w / 2).toFixed(2) + 'px';
    this.shape.style.backgroundColor = 'rgb(' + c.join(',') + ')';
    for (var name in LAYERS) {
      if (this.layers[name]) show(this.layers[name], layerAlpha(LAYERS[name], t));
    }

    // ── 1 Get in ──
    var typing = t < 21 ? smooth(0.55, 1.25, t) : 0;
    var count = Math.round(typing * this.email.length);
    setText(this.typed, this.email.slice(0, count));
    this.placeholder.style.opacity = count === 0 ? 1 : 0;
    var caretOn = t > 0.5 && t < 1.62 && (t < 1.3 || Math.floor(t * 3) % 2 === 0);
    this.caret.style.opacity = caretOn ? 1 : 0;
    this.sendBtn.style.transform = 'translateY(-50%) scale(' + (1 - 0.06 * pulse(1.58, 1.78, t)).toFixed(3) + ')';

    var confirmed = t < 4 && smooth(3.0, 3.12, t) > 0.5;
    for (i = 0; i < this.slots.length; i++) {
      var f = smooth(2.3 + i * 0.1, 2.38 + i * 0.1, t);
      var dot = this.slots[i].firstElementChild;
      dot.style.transform = 'scale(' + f.toFixed(3) + ')';
      this.slots[i].style.borderColor = confirmed ? GREEN : (f > 0.5 ? NAVY : '');
      dot.style.backgroundColor = confirmed ? GREEN : NAVY;
    }
    var open = smooth(3.8, 4.05, t);
    this.shackle.style.transform = 'translateY(' + (-4 * open).toFixed(2) + 'px) rotate(' + (-28 * open).toFixed(2) + 'deg)';
    var label = smooth(3.95, 4.2, t);
    this.unlockText.style.opacity = label.toFixed(3);
    this.unlockText.style.transform = 'translateX(' + (8 * (1 - label)).toFixed(2) + 'px)';

    // ── 2 Find ──
    var searched = t < 8 ? smooth(5.35, 5.85, t) : 0;
    setText(this.searchTyped, this.term.slice(0, Math.round(searched * this.term.length)));
    this.searchCaret.style.opacity = t > 5.3 && t < 6.0 && (t < 5.85 || Math.floor(t * 3) % 2 === 0) ? 1 : 0;

    var chipOn = t < 9 ? smooth(6.85, 6.95, t) : 0;
    for (i = 0; i < this.chips.length; i++) {
      var mine = i === this.chipPick;
      this.chips[i].style.setProperty('--bx-on', (mine ? chipOn : 0).toFixed(3));
      scaleCentered(this.chips[i], 1 - 0.06 * (mine ? pulse(6.8, 6.98, t) : 0));
    }
    // Results arrive one by one; the filter drops rows that don't come in
    // that colour, the rest slide up and switch to that colour's photo.
    var live = t < 9;
    var filtered = live ? smooth(6.95, 7.3, t) : 0;
    var swap = live ? smooth(7.0, 7.25, t) : 0;
    var slot = 0;
    for (i = 0; i < this.resultRows.length; i++) {
      var row = this.resultRows[i];
      var arrive = smooth(6.25 + i * 0.08, 6.45 + i * 0.08, t);
      var matches = row.hasAttribute('data-bx-match');
      var baseY = -22 + i * 60;
      var y = matches ? lerp(baseY, -22 + slot * 60, filtered) : baseY;
      var alpha = matches ? arrive : arrive * (1 - (live ? smooth(6.9, 7.15, t) : 0));
      row.style.top = y.toFixed(2) + 'px';
      row.style.opacity = alpha.toFixed(3);
      row.style.transform = 'translateY(-50%) scale(' + (matches ? 1 : 1 - 0.05 * filtered).toFixed(3) + ')';
      row.style.setProperty('--bx-press', (matches && slot === 0 && live ? pulse(7.82, 8.05, t) : 0).toFixed(3));
      row.style.setProperty('--bx-swap', (matches ? swap : 0).toFixed(3));
      if (matches) slot++;
    }
    if (this.resultCount) this.resultCount.style.opacity = (live ? smooth(7.25, 7.45, t) : 0).toFixed(3);

    // ── 3 Order ──
    var qty = t < 9.1 || t > 12.5 ? '1' : t < 9.4 ? '2' : '3';
    if (qty !== this.lastQty) { this.qty.textContent = qty; this.lastQty = qty; }
    var bump = Math.max(pulse(9.1, 9.25, t, 0.05, 0.15), pulse(9.4, 9.55, t, 0.05, 0.15));
    scaleCentered(this.qty, 1 + 0.25 * bump);
    scaleCentered(this.plus, 1 - 0.1 * Math.max(pulse(9.03, 9.17, t), pulse(9.33, 9.47, t)));
    for (i = 0; i < this.photos.length; i++) {
      this.photos[i].style.opacity = i === this.pick ? '1' : '0';
    }

    // Spinner turns with the clock, straight into the check.
    this.spin.style.transform = 'rotate(' + ((t * 450) % 360).toFixed(1) + 'deg)';
    this.checkPath.style.strokeDashoffset = (1 - smooth(11.23, 11.45, t)).toFixed(3);

    // Quick Order: quantities go into colour/size cells; the product badge
    // and the bottom bar count up (as on the real page), then Add selected.
    var inQo = t < 15.6;
    var total = 0;
    for (i = 0; i < this.qoRows.length; i++) {
      this.qoRows[i].style.opacity = smooth(12.8 + i * 0.07, 13.0 + i * 0.07, t).toFixed(3);
    }
    for (i = 0; i < this.qoInputs.length; i++) {
      var typed = inQo && t >= QO_TYPE[i];
      var value = typed ? QO_QTY[i % QO_QTY.length] : 0;
      total += value;
      setText(this.qoInputs[i], String(value));
      this.qoInputs[i].classList.toggle('is-set', typed);
      var focus = inQo && t > QO_TYPE[i] - 0.04 && t < QO_TYPE[i] + 0.3 ? 1 : 0;
      this.qoInputs[i].style.setProperty('--bx-focus', focus);
    }
    var added = inQo ? smooth(14.85, 15.0, t) : 0;
    if (this.qoBadge) setText(this.qoBadge, total + ' selected');
    var qoStyles = total ? 1 : 0;
    if (this.qoStatus) setText(this.qoStatus, total + ' units · ' + qoStyles + (qoStyles === 1 ? ' style' : ' styles'));
    if (this.qoBtnText) setText(this.qoBtnText, added > 0.5 ? 'Added ✓' : 'Add selected to cart');
    if (!this.qoBtn) total = 0;
    else this.qoBtn.style.setProperty('--bx-ready', total ? '1' : '0');
    if (this.qoBtn) {
      this.qoBtn.style.setProperty('--bx-done', added.toFixed(3));
      scaleCentered(this.qoBtn, 1 - 0.05 * pulse(14.72, 14.9, t));
    }

    // ── 4 Receive ──
    var top = valueAt(EDGES.top, t);
    var bottom = valueAt(EDGES.bottom, t);
    this.indicator.style.top = top.toFixed(2) + 'px';
    this.indicator.style.height = Math.max(8, bottom - top).toFixed(2) + 'px';
    this.indicator.style.opacity = smooth(15.7, 15.85, t).toFixed(3);
    var picked = smooth(17.55, 17.67, t);
    var hovered = -1;
    for (var v = SHIP_VISITS.length - 1; v >= 0; v--) {
      if (t >= SHIP_VISITS[v][0] - 0.05) { hovered = SHIP_VISITS[v][1]; break; }
    }
    for (i = 0; i < this.rows.length; i++) {
      this.rows[i].classList.toggle('is-hover', i === hovered);
      this.rows[i].style.setProperty('--bx-picked', i === SHIP_PICK ? picked.toFixed(3) : '0');
    }
    var p = smooth(18.25, 19.15, t);
    this.trackFill.style.width = (280 * p).toFixed(2) + 'px';
    this.truck.style.left = (-140 + 280 * p).toFixed(2) + 'px';
    for (i = 0; i < this.nodes.length; i++) {
      this.nodes[i].style.setProperty('--bx-lit', smooth(i / 2 - 0.08, i / 2, p).toFixed(3));
    }

    // ── 5 Reorder ──
    for (i = 0; i < this.reorderThumbs.length; i++) {
      var th = smooth(19.65 + i * 0.08, 19.85 + i * 0.08, t);
      this.reorderThumbs[i].style.opacity = th.toFixed(3);
      this.reorderThumbs[i].style.transform = 'translateY(calc(-50% + ' + (8 * (1 - th)).toFixed(2) + 'px))';
    }
    var buying = smooth(20.08, 20.18, t);
    var bought = smooth(20.6, 20.72, t);
    this.buyLabel.style.opacity = (1 - buying).toFixed(3);
    this.buySpin.style.opacity = (buying * (1 - bought)).toFixed(3);
    this.buySpin.style.transform = 'translate(-50%, -50%) rotate(' + ((t * 450) % 360).toFixed(1) + 'deg)';
    this.buyDone.style.opacity = bought.toFixed(3);
    this.buyBtn.style.setProperty('--bx-done', bought.toFixed(3));
    scaleCentered(this.buyBtn, 1 - 0.05 * pulse(19.98, 20.15, t));

    // ── Cursor ──
    var pos = this.cursorAt(t);
    var press = 0;
    CLICKS.forEach(function (ck) { press = Math.max(press, pulse(ck - 0.07, ck + 0.14, t, 0.07, 0.12)); });
    this.cursor.style.transform = 'translate(' + pos[0].toFixed(2) + 'px, ' + pos[1].toFixed(2) + 'px) scale(' + (1 - 0.18 * press).toFixed(3) + ')';

    // ── Captions ──
    for (i = 0; i < this.captionEls.length; i++) {
      var capEl = this.captionEls[i];
      var ca = captionAlpha(CAPTIONS[capEl.getAttribute('data-bx-cap')] || [], t);
      capEl.style.opacity = ca.toFixed(3);
      capEl.style.filter = ca > 0.001 && ca < 0.999 ? 'blur(' + ((1 - ca) * 4).toFixed(2) + 'px)' : '';
      capEl.style.visibility = ca > 0.001 ? 'visible' : 'hidden';
    }

    // ── Chapter bar ──
    var idx = chapterIndex(t);
    for (var s = 0; s < this.fills.length; s++) {
      var fill = s < idx ? 1 : s > idx ? 0 : clamp01((t - CHAPTERS[s]) / (CHAPTERS[s + 1] - CHAPTERS[s]));
      this.fills[s].style.transform = 'scaleX(' + fill.toFixed(4) + ')';
      this.segments[s].setAttribute('aria-current', s === idx ? 'step' : 'false');
    }
    for (var k = 0; k < this.captions.length; k++) {
      this.captions[k].classList.toggle('is-active', k === idx);
    }
  };

  Explainer.prototype.cursorAt = function (t) {
    var path = this.path;
    for (var i = 0; i < path.length - 1; i++) {
      var a = path[i];
      var b = path[i + 1];
      if (t >= a[0] && t <= b[0]) {
        var k = smooth(a[0], b[0], t);
        return [lerp(a[1], b[1], k), lerp(a[2], b[2], k)];
      }
    }
    var last = path[path.length - 1];
    return [last[1], last[2]];
  };

  function init() {
    document.querySelectorAll('[data-eu-bx]').forEach(function (root) {
      if (!root.euExplainer) root.euExplainer = new Explainer(root);
    });
  }

  // Exposed for the theme editor and for rendering frames on demand.
  window.EuBuyingExplainer = {
    init: init, loop: LOOP, realLoop: REAL_LOOP, hold: HOLD, chapters: CHAPTERS, authoredAt: authoredAt
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
  document.addEventListener('shopify:section:load', init);
}());
